/**
 * First-touch attribution capture (iter-52.25).
 *
 * Reads UTM + Facebook/Google click IDs + landing URL + referrer from
 * the browser on FIRST page load, and stores them in `localStorage`
 * under a namespaced key. Once stored, the values are NEVER overwritten
 * by later visits — this is "first-touch" attribution, which is what
 * Leena wants for Facebook ad ROI (the specific ad that first brought
 * a user in, not whichever tab they happened to be on when they
 * finally signed up).
 *
 * Exports:
 *   • captureAttributionOnLoad() — call once from `index.js` on app boot.
 *   • getAttribution() — returns the stored dict (or null if empty).
 *   • clearAttribution() — for the "Sign out and forget me" affordance
 *     (not currently wired; keeps the API symmetrical).
 */

const KEY = 'rb_first_touch_attribution';
// Params we care about. `fbclid` = Facebook click ID (auto-attached by
// FB when a user clicks any FB ad); `gclid` = Google Ads equivalent.
const UTM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign',
  'utm_term', 'utm_content', 'fbclid', 'gclid',
];

const _hasStorage = () => {
  try { return typeof window !== 'undefined' && !!window.localStorage; }
  catch { return false; }
};

export function captureAttributionOnLoad() {
  if (!_hasStorage()) return;
  // First-touch: bail if we already captured on a previous visit.
  try {
    if (window.localStorage.getItem(KEY)) return;
  } catch { return; }

  const params = new URLSearchParams(window.location.search || '');
  const payload = {};
  for (const k of UTM_KEYS) {
    const v = (params.get(k) || '').trim();
    if (v) payload[k] = v.slice(0, 200); // cap so a malicious/huge value can't blow up Mongo
  }
  const referrer = (document.referrer || '').trim().slice(0, 500);
  if (referrer) payload.http_referrer = referrer;
  payload.first_landing_url = (window.location.href || '').slice(0, 500);
  payload.first_touch_at = new Date().toISOString();

  // If NOTHING interesting was captured (direct visit, no UTM, no
  // referrer), still stash a minimal marker so we don't keep re-running
  // the capture on every page load. Backend can inspect
  // `first_touch_at` to distinguish "unknown source" from "never
  // captured" if it ever matters.
  try { window.localStorage.setItem(KEY, JSON.stringify(payload)); }
  catch { /* quota exceeded or blocked — silent */ }
}

export function getAttribution() {
  if (!_hasStorage()) return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function clearAttribution() {
  if (!_hasStorage()) return;
  try { window.localStorage.removeItem(KEY); } catch {}
}
